// GEM_PREP_POC: Gem Prep Pilot — limited deployment, not for general use. See https://www.notion.so/magicschoolai/Knowledge-Graph-Gem-Prep-v1-Requirements-2fc31e9c462b80f187adca9cc34fbf44
import { DEFAULT_ADJUDICATION_PROMPT_TUNING } from '@magicschool/moderation/constants/adjudicationPromptTuning';
import { DEFAULT_RAW_ESCALATION_THRESHOLDS } from '@magicschool/moderation/constants/thresholds';
import type { ClientSiteConfigs, MaintenanceRaw, MaintenanceWindow, SiteConfigs, TosUpdateWindow, TosUpdateWindowRaw } from './types';

const MILLISECONDS_IN_HOUR = 60 * 60 * 1000;

export const defaultSiteConfigs: SiteConfigs = {
  featureFlags: {
    disabledSubscriptions: false,
    magicStudentEnabledForUser: false,
    magicStudentEnabledForOrg: false,
    pdfUploadEnabled: false,
    youtubeImportEnabled: false,
    websiteImportEnabled: false,
    studentSummariesEnabled: false,
    threadSummariesEnabled: false,
    studentRoomLocaleEnabled: false,
    skilljarEnabled: false,
    bulkWritingFeedbackEnabledForOrg: true,
    bulkWritingFeedbackEnabledForUser: true,
    quizzesEnabledForOrg: true,
    quizzesEnabledForUser: true,
    cwfGradeEnabledForOrg: true,
    cwfGlowsGrowsEnabledForOrg: true,
    cwfNextStepsEnabledForOrg: true,
    teacherExportStudentThreadsEnabledForOrg: false,
    studentExportStudentThreadsEnabledForOrg: false,
    authenticWritingFeedbackApprovalRequired: false,
    cwfCanvasEnabledForOrg: false,
    cwfSchoologyEnabledForOrg: false,
    gemPrepInsightsEnabledForOrg: false, // GEM_PREP_POC
    gemPrepInsightsEnabledForUser: false, // GEM_PREP_POC
    hasLearningOutcomesSku: false,
    knowledgeEnabled: true,
    overrideEnableRoomUnlockForFreeUsers: false,
  },
  globalModerationConfig: {
    keywordListId: '',
  },
  knowledgeBaseConfig: {
    searchInstruction: '',
    selectedResourcesInstruction:
      "Selected Resources\n\nThe user selected the following resources from their personal Resource Library:\n\n{listing}\n\nFor uploaded documents, use the searchDocuments tool to search their content. For studio documents, use readStudioModeDocument(documentId). For chat messages, use readChatMessage(messageId). Use these tools now if they would help you address the user's message.",
    embedding: {
      model: 'text-embedding-3-small',
      dimensions: 1536,
      maxRetries: 3,
    },
    retrieval: {
      topK: 10,
      similarityThreshold: 0.1,
      mmrLambda: 0.7,
      rerankerThreshold: 0.6,
      rerankerQualityFloor: 0.3,
      rrfThreshold: 0.01,
      rrfQualityFloor: 0.005,
      enableFts: true,
      enableMmr: true,
      minChunksToReturn: 3,
      overfetchMultiplier: 5,
      embeddingBatchMaxTokens: 300000,
      enableReranker: true,
      rerankerRegion: 'us-west-2',
      rerankerFailoverRegions: ['us-east-1', 'ca-central-1'],
      rerankerModelId: 'cohere.rerank-v3-5:0',
      skillResolutionFloor: 0.06,
      writerGuidanceFloor: 0.08,
    },
    tokenizer: {
      encoding: 'cl100k_base',
      safeTokens: 7500,
    },
    vectorStore: {
      batchSize: 100,
      dedupPrefixLength: 200,
      minFtsQueryLength: 3,
    },
    proactive: {
      topK: 10,
      lowerThreshold: 0.01,
      higherThreshold: 0.015,
      maxChars: 30000,
      docTitleListingMax: 20,
      mentionDirective: '',
      contentInstruction: '',
      resultsFoundMessage: '{count} relevant results found.',
      unknownDocumentLabel: 'Unknown document',
    },
  },
  maintenance: null,
  userBlocking: {
    autoBlockEnabled: true,
  },
  tosUpdateWindows: [],
  knowledgeBaseIngestionPrompts: {
    extractionSystemPrompt: '',
    tocPrompt: '',
    extractionUserPromptTemplate: '',
    extractionSchemaDescriptions: {
      chunkContent: '',
      chunkSummary: '',
      chunkSectionPath: '',
      documentTitle: '',
      page1SchemaName: '',
      page1SchemaDescription: '',
      otherPagesSchemaName: '',
      otherPagesSchemaDescription: '',
    },
    pageInfoTemplates: {
      twoPages: '',
      onePage: '',
    },
    partLabelTemplate: '',
    tocTemplates: {
      headingTemplate: '',
      entryTemplate: '',
      summaryTemplate: '',
      sectionPathLabel: '',
      partLabelTemplate: '',
    },
    page1SchemaExample: '',
    otherPagesSchemaExample: '',
  },
  systemMessages: {
    globalStudentToolSystemMessage: '',
    globalTeacherToolSystemMessage: '',
    globalLocaleTranslationBasic: '',
    globalLocaleTranslationStrict: '',
    globalLocaleTranslationDialect: '',
    globalAdditionalKnowledgeMessage: '',
    globalThreadInsightsMessage: '',
    globalRoomStudentInsightsMessage: '',
    globalStandardsToolMessage: '',
    migratedChatbotOpeningPrompt: '',
    migratedChatbotDefaultPersona: '',
    promptAssistantForTools: '',
    promptAssistantForRagBotsPrompt: '',
    promptAssistantForRagBotsSystemMessage: '',
    rubricGeneratorV2SystemMessage: '',
    rainaToolCallingSystemMessage: '',
    reviewerGradeFeedbackPrompt: '',
    reviewerGradeFeedbackV2Prompt: '',
    reviewerGlowsGrowsPrompt: '',
    reviewerNextStepsPrompt: '',
    studioModePrompt: '',
    llmJudgeModerationPrompt: '',
    worksheetRefinementRules: '',
    worksheetAnswerKeyOmitHint: '',
    worksheetAnswerKeyIncludeHint: '',
  },
  moderationPipelineConfig: {
    escalationThresholds: DEFAULT_RAW_ESCALATION_THRESHOLDS,
    adjudicationPromptTuning: DEFAULT_ADJUDICATION_PROMPT_TUNING,
  },
};

export function toClientSiteConfigs(configs: SiteConfigs): ClientSiteConfigs {
  return {
    featureFlags: configs.featureFlags,
    maintenance: configs.maintenance,
    tosUpdateWindows: configs.tosUpdateWindows,
    globalModerationConfig: configs.globalModerationConfig,
  } as ClientSiteConfigs;
}

export const defaultClientSiteConfigs: ClientSiteConfigs = toClientSiteConfigs(defaultSiteConfigs);

function parseMaintenanceDate(value: string | number | undefined | null): Date | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function resolveMaintenanceMode(raw: MaintenanceRaw): MaintenanceWindow['mode'] {
  if (raw.mode) {
    return raw.mode;
  }

  if (raw.borkedPeriod) {
    return 'serviceInconsistent';
  }

  const hasEndDateTime = parseMaintenanceDate(raw.endDateTime) !== null;
  const hasHoursToWarnBefore = raw.hoursToWarnBefore !== undefined && raw.hoursToWarnBefore !== null;

  if (hasEndDateTime || hasHoursToWarnBefore) {
    return 'siteMaintenance';
  }

  return 'serviceInterruption';
}

export function convertMaintenanceValuesIntoDates(maintenance: MaintenanceRaw | null | undefined): SiteConfigs['maintenance'] {
  try {
    if (!maintenance?.startDateTime) {
      return null;
    }

    const maintenanceStartDateTime = parseMaintenanceDate(maintenance.startDateTime);
    if (!maintenanceStartDateTime) {
      return null;
    }

    const maintenanceEndDateTime = parseMaintenanceDate(maintenance.endDateTime);
    const hoursToWarnBefore = maintenance.hoursToWarnBefore ? Number.parseInt(maintenance.hoursToWarnBefore.toString(), 10) : null;
    const optionalDay = parseMaintenanceDate(maintenance.optionalBorkedDay);
    const mode = resolveMaintenanceMode(maintenance);

    const maintenanceWindow: MaintenanceWindow = {
      startDateTime: maintenanceStartDateTime,
      mode,
      labelId: maintenance.labelId ? maintenance.labelId : null,
      borkedFunction: maintenance.borkedFunction ? maintenance.borkedFunction : null,
      borkedPeriod: maintenance.borkedPeriod ? maintenance.borkedPeriod : null,
      optionalBorkedDay: optionalDay,
    };

    if (mode === 'siteMaintenance') {
      if (maintenanceEndDateTime) {
        maintenanceWindow.endDateTime = maintenanceEndDateTime;
      }
      if (hoursToWarnBefore) {
        maintenanceWindow.showWarningBannerDateTime = new Date(
          maintenanceStartDateTime.getTime() - hoursToWarnBefore * MILLISECONDS_IN_HOUR,
        );
      }
    }

    return maintenanceWindow;
  } catch {
    return null;
  }
}

export function convertTosWindow(key: string, window: TosUpdateWindowRaw): TosUpdateWindow {
  return {
    windowKey: key,
    startDateTime: new Date(window.startDateTime),
    endDateTime: new Date(window.endDateTime),
    messageId: window.messageId ? window.messageId : 'tos.defaultMessageId',
  };
}
